import { useEffect, useState } from 'react';
import Dashboard, { DashboardSection, DashboardSectionTitle } from '../../components/dashboard';
import { DashboardSurveyType, GridDashboardItem, InsightDashboard } from '../../types/insight-custom-dashboard';
import { CompanyInsightsSidebar } from '../summary/insights/partials/sidebar/CompanyInsightsSidebar';
import { CustomDashboard } from './CustomDashboard';
import { CustomDashboardEditing } from './CustomDashboardEditing';
import { useHistory, useParams } from 'react-router-dom';
import {
  useDeleteInsightDashboardMutation,
  useDuplicateInsightDashboardMutation,
  useGetInsightDashboardByIdQuery,
  useUpdateInsightDashboardMutation,
} from '../../api/insight-dashboards';
import { generateUrl } from '../util';
import { ROUTES } from '../../constants/routes';
import Loader from '../../components/loader';
import { BasicAlert } from '../../components/alert/BasicAlert';
import { RootState, useAppSelector } from '../../reducers';
import { canManageCurrentLevel, getCurrentUser } from '../../selectors/user';
import { FeaturePermissions } from '../../services/permissions/FeaturePermissions';
import { hasUtrvHistoryModal } from './utils';
import {
  DashboardItemFilters,
  HistoricalUtrs,
  HistoricalUtrsQueryByCodeParams,
  useLazyGetHistoricalUtrsByCodesQuery,
} from '../../api/insights';
import UniversalTrackerModalService from '../../model/UniversalTrackerModalService';
import { UtrvHistoryModal } from './utrv-history-modal/UtrvHistoryModal';
import { Tab } from '../../components/utr-modal/ContentTabs';
import NotAuthorised from '../not-authorised';
import { API } from '../../constants/errors';
import { selectMostRecentSurveyByPeriodAndType } from '../../slice/initiativeSurveyListSlice';
import { useGetSurveyByIdQuery } from '../../api/surveys';
import { DataPeriods } from '../../types/universalTracker';
import { useGetAvailablePeriodsQuery } from '../../api/initiatives';
import { TOOLTIP_MESSAGE } from '../summary/insights/utils/sidebar';
import { CustomDashboardWrapper } from './context/CustomDashboardWrapper';
import LoadingPlaceholder from '@components/LoaderContainer/LoadingPlaceholder';
import { SurveyType } from '@g17eco/types/survey';
import { getDefaultPeriod } from '@components/survey-period-dropdown/utils';
import { Filters } from '@components/utr-modal/SurveyFilters';

export const CustomDashboardContainer = () => {
  const history = useHistory();
  const { dashboardId, initiativeId } = useParams<{ initiativeId: string; dashboardId: string }>();
  const [period, setPeriod] = useState<DataPeriods | undefined>();
  const [surveyType, setSurveyType] = useState<DashboardSurveyType>(SurveyType.Default);

  // @TODO Might need to abstract company tracker related redux state into a wrapper
  // when refactoring this for portfolio tracker use.
  const { data: availablePeriods = [], isFetching: isFetchingPeriods } = useGetAvailablePeriodsQuery({
    initiativeId,
    surveyType,
  });
  const {
    data: dashboard,
    isFetching,
    isError,
    error,
    refetch: refetchDashboard,
  } = useGetInsightDashboardByIdQuery(
    { dashboardId, initiativeId, queryParams: { period, surveyType } },
    { skip: isFetchingPeriods }
  );
  const [updateDashboard, { isLoading: isUpdating, isError: isUpdatingError }] = useUpdateInsightDashboardMutation();
  const [deleteDashboard, { isLoading: isDeleting, isError: isDeletingError }] = useDeleteInsightDashboardMutation();
  const [duplicateDashboard] = useDuplicateInsightDashboardMutation();
  const [getHistoricalUtrsByCodes, { isLoading: isLoadingUtrsData }] = useLazyGetHistoricalUtrsByCodesQuery();

  const canAccessCustomDashboards = useAppSelector(FeaturePermissions.canAccessCustomDashboards);
  const initiative = useAppSelector((state: RootState) => state.initiative.data);
  const currentUser = useAppSelector(getCurrentUser);
  const mostRecentSurvey = useAppSelector((state: RootState) =>
    selectMostRecentSurveyByPeriodAndType(state, period, surveyType)
  );

  const { data: latestSurvey } = useGetSurveyByIdQuery(mostRecentSurvey?._id ?? '', {
    skip: !mostRecentSurvey?._id,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [selectingUtrData, setSelectingUtrData] = useState<UniversalTrackerModalService | undefined>(undefined);

  const isCurrentLevelDashboard = initiativeId === dashboard?.initiativeId;
  const canManage = useAppSelector(canManageCurrentLevel);

  useEffect(() => {
    setPeriod(getDefaultPeriod(availablePeriods));
  }, [availablePeriods]);

  if (error?.message === API.NOT_PERMITTED) {
    return <NotAuthorised />;
  }

  if (!dashboard || !initiative) {
    return (
      <Dashboard className='profile-dashboard insights-dashboard' hasSidebar>
        <CompanyInsightsSidebar alignButtonRow={false} />
        <LoadingPlaceholder height={600} />
      </Dashboard>
    );
  }

  const handleGetHistoricalUtrsByCodes = (params: HistoricalUtrsQueryByCodeParams) => {
    return getHistoricalUtrsByCodes(params).unwrap();
  };

  const handleClickEdit = () => setIsEditing(true);
  const handleCancel = () => {
    setIsEditing(false);
  };
  const handleSave = async (changes: Partial<InsightDashboard>, keepEditing?: boolean) => {
    await updateDashboard({ ...dashboard, ...changes, initiativeId });

    if (!keepEditing) {
      setIsEditing(false);
    }
  };
  const handleDelete = async () => {
    await deleteDashboard({ dashboardId, initiativeId });
    setIsEditing(false);
    const url = generateUrl(ROUTES.SUMMARY, { dashboardId, initiativeId });
    history.push(url);
  };

  const handleDuplicateDashboard = () => {
    return duplicateDashboard({ dashboardId, initiativeId }).unwrap();
  };

  const generateSelectingUtrData = ({ utrData, activeTab }: { utrData: HistoricalUtrs; activeTab?: Tab['navId'] }) => {
    const modalService = new UniversalTrackerModalService(utrData.utr);
    modalService.setUniversalTrackerValues(utrData.utrvs);
    modalService.setInitiativeId(initiativeId || dashboard.initiativeId);
    modalService.setActiveTabId(activeTab);

    setSelectingUtrData(modalService);
  };

  const handleOpenUtrvHistoryModal = ({
    item,
    utrData,
    activeTab,
  }: {
    item: GridDashboardItem;
    utrData?: HistoricalUtrs;
    activeTab?: Tab['navId'];
  }) => {
    if (!hasUtrvHistoryModal(item) || !utrData) {
      return;
    }

    generateSelectingUtrData({ utrData, activeTab });
  };

  const reloadUtrvs = async () => {
    return refetchDashboard()
      .unwrap()
      .then((dashboard) => {
        const utrData = dashboard.utrsData.find(
          (data) => data.utr.code === selectingUtrData?.getUniversalTracker().getCode()
        );

        if (utrData) {
          return generateSelectingUtrData({ utrData });
        }
      });
  };

  const handleChangeUtrvHistoryModalFilters = (filters: Filters) => {
    if (!selectingUtrData) {
      return;
    }

    const queryParams: DashboardItemFilters = {
      period: filters.period === 'all' ? undefined : filters.period,
      surveyType: filters.surveyType === 'all' ? undefined : filters.surveyType,
      ...dashboard.filters,
    };

    handleGetHistoricalUtrsByCodes({
      initiativeId,
      utrCodes: [selectingUtrData.getUniversalTracker().getCode()],
      queryParams,
    }).then((result) => {
      if (!result[0]) {
        return;
      }

      return generateSelectingUtrData({ utrData: result[0] });
    });
  };

  const renderCustomDashboard = () => {
    if (!canAccessCustomDashboards) {
      return (
        <Dashboard className='w-100'>
          <DashboardSection>
            <DashboardSectionTitle title={TOOLTIP_MESSAGE.NOT_AVAILABLE_PLAN} />
          </DashboardSection>
        </Dashboard>
      );
    }

    return (
      <CustomDashboardWrapper
        getHistoricalUtrsByCodes={handleGetHistoricalUtrsByCodes}
        duplicateDashboard={handleDuplicateDashboard}
      >
        {isFetching || isUpdating || isDeleting || isLoadingUtrsData ? <Loader /> : null}
        {selectingUtrData ? (
          <UtrvHistoryModal
            canAccessCustomDashboards={canAccessCustomDashboards}
            reloadUtrvs={reloadUtrvs}
            toggle={() => setSelectingUtrData(undefined)}
            modalService={selectingUtrData}
            currentUser={currentUser}
            showTargetBaselineAdding={canManage}
            disableTargetBaselineAdding={isEditing}
            activeTabId={selectingUtrData.getActiveTabId()}
            initialFilters={{ surveyType, period: period ?? 'all' }}
            onChangeFilters={handleChangeUtrvHistoryModalFilters}
          />
        ) : null}
        {isEditing ? (
          <CustomDashboardEditing
            dashboard={dashboard}
            handleCancel={handleCancel}
            handleSave={handleSave}
            handleDelete={handleDelete}
            handleOpenUtrvHistoryModal={handleOpenUtrvHistoryModal}
            initiative={initiative}
            survey={latestSurvey}
            availablePeriods={availablePeriods}
            period={period}
            surveyType={surveyType}
          />
        ) : (
          <CustomDashboard
            dashboard={dashboard}
            handleClickEdit={handleClickEdit}
            handleSave={handleSave}
            canManage={canManage}
            isCurrentLevelDashboard={isCurrentLevelDashboard}
            handleOpenUtrvHistoryModal={handleOpenUtrvHistoryModal}
            initiative={initiative}
            survey={latestSurvey}
            availablePeriods={availablePeriods}
            period={period}
            setPeriod={(newPeriod) => setPeriod(newPeriod)}
            surveyType={surveyType}
            setSurveyType={(newSurveyType) => setSurveyType(newSurveyType)}
          />
        )}
      </CustomDashboardWrapper>
    );
  };

  return (
    <Dashboard className='profile-dashboard insights-dashboard' hasSidebar>
      <CompanyInsightsSidebar alignButtonRow={false} />
      {isError || isUpdatingError || isDeletingError ? <BasicAlert type={'danger'}>Something wrong</BasicAlert> : null}
      {renderCustomDashboard()}
    </Dashboard>
  );
};
